import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { configureApiClient, apiFetch } from "@/auth/apiClient";

export type UiRole = "student" | "teacher" | "admin";
export type BackendRole = "STUDENT" | "TEACHER" | "SYSTEM_ADMIN";

export type PermissionKey =
  | "create_course"
  | "create_exam"
  | "edit_course"
  | "manage_students"
  | "moderate_community"
  | "view_analytics"
  | "manage_certificates";

const ALL_PERMISSION_KEYS: PermissionKey[] = [
  "create_course",
  "create_exam",
  "edit_course",
  "manage_students",
  "moderate_community",
  "view_analytics",
  "manage_certificates",
];

const emptyPermissions: Record<PermissionKey, boolean> = ALL_PERMISSION_KEYS.reduce(
  (acc, k) => {
    acc[k] = false;
    return acc;
  },
  {} as Record<PermissionKey, boolean>
);

export type AuthUser = {
  id: number;
  email: string;
  role: UiRole;
  backendRole: BackendRole;
  userType: "user" | "admin";
  permissions: Record<PermissionKey, boolean>;
};

type Tokens = { accessToken: string; refreshToken: string };

type AuthContextValue = {
  user: AuthUser | null;
  tokens: Tokens | null;
  isHydrating: boolean;
  login: (email: string, password: string) => Promise<void>;
  requestRegistrationOtp: (email: string) => Promise<void>;
  register: (payload: Record<string, unknown>) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = "komorebi.auth.v1";

function mapRole(backendRole: string): UiRole {
  switch (backendRole) {
    case "TEACHER":
      return "teacher";
    case "SYSTEM_ADMIN":
      return "admin";
    case "STUDENT":
    default:
      return "student";
  }
}

function loadStored(): { user: AuthUser; tokens: Tokens } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as any;
    if (!parsed?.tokens?.accessToken || !parsed?.tokens?.refreshToken || !parsed?.user?.email) return null;
    return { user: parsed.user as AuthUser, tokens: parsed.tokens as Tokens };
  } catch {
    return null;
  }
}

function persist(next: { user: AuthUser; tokens: Tokens } | null) {
  if (!next) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function readStoredAuth(): { user: AuthUser; tokens: Tokens } | null {
  if (typeof window === "undefined") return null;
  return loadStored();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const apiUrl = import.meta.env.VITE_API_URL ?? "http://127.0.0.1:3025";

  /** Restore from localStorage on first render so verifyToken runs with Bearer set (fixes refresh → login). */
  const [user, setUser] = useState<AuthUser | null>(() => {
    const storedUser = readStoredAuth()?.user as any | null | undefined;
    if (!storedUser) return null;
    return {
      ...storedUser,
      permissions:
        storedUser.permissions && typeof storedUser.permissions === "object"
          ? storedUser.permissions
          : emptyPermissions,
    } as AuthUser;
  });
  const [tokens, setTokens] = useState<Tokens | null>(() => readStoredAuth()?.tokens ?? null);
  const [isHydrating, setIsHydrating] = useState(() => Boolean(readStoredAuth()));

  const clearAuth = useCallback(() => {
    setUser(null);
    setTokens(null);
    persist(null);
  }, []);

  const userRef = useRef(user);
  userRef.current = user;

  useLayoutEffect(() => {
    configureApiClient({
      baseUrl: String(apiUrl),
      getTokens: () => tokens,
      setTokens: (t) => {
        if (!t) return clearAuth();
        setTokens(t);
        const current = userRef.current;
        if (current) persist({ user: current, tokens: t });
      },
      onUnauthorized: clearAuth
    });
  }, [apiUrl, tokens, user, clearAuth]);

  useEffect(() => {
    const stored = readStoredAuth();
    if (!stored) {
      setIsHydrating(false);
      return;
    }

    const hydrate = async () => {
      try {
        await apiFetch("/auth/user/verifyToken");
        const perms = await apiFetch<Record<PermissionKey, boolean>>(
          "/auth/user/me/permissions"
        );
        setUser((prev) => (prev ? { ...prev, permissions: perms } : prev));
      } catch (err: any) {
        console.error("Hydration failed error:", err?.message || err);
        clearAuth();
      } finally {
        setIsHydrating(false);
      }
    };

    void hydrate();
  }, [clearAuth]);

  async function fetchMyPermissionsWithAccessToken(
    accessToken: string,
  ): Promise<Record<PermissionKey, boolean>> {
    try {
      const res = await fetch(`${apiUrl}/auth/user/me/permissions`, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return emptyPermissions;
      const data = (await res.json()) as Record<PermissionKey, boolean>;
      return { ...emptyPermissions, ...data };
    } catch {
      return emptyPermissions;
    }
  }

  const login = useCallback(async (email: string, password: string) => {
    const res = (await apiFetch<any>("/auth/user/login", { method: "POST", json: { email, password } })) as any;
    const accessToken = String(res.accessToken);
    const refreshToken = String(res.refreshToken);
    const backendRole = String(res.user?.role ?? "STUDENT") as BackendRole;
    const userType = String(res.user?.userType ?? "user") as "user" | "admin";

    const permissions = await fetchMyPermissionsWithAccessToken(accessToken);

    const nextUser: AuthUser = {
      id: Number(res.user?.id),
      email: String(res.user?.email ?? email),
      backendRole,
      role: mapRole(backendRole),
      userType,
      permissions,
    };
    const nextTokens: Tokens = { accessToken, refreshToken };
    setUser(nextUser);
    setTokens(nextTokens);
    persist({ user: nextUser, tokens: nextTokens });
  }, []);

  const requestRegistrationOtp = useCallback(async (email: string) => {
    await apiFetch("/auth/user/email-verification", { method: "POST", json: { email } });
  }, []);

  const register = useCallback(async (payload: Record<string, unknown>) => {
    const body = { ...payload };
    if (body.otpCode !== undefined && typeof body.otpCode === "string") {
      body.otpCode = Number.parseInt(String(body.otpCode).trim(), 10);
    }
    const res = (await apiFetch<any>("/auth/user/register", { method: "POST", json: body })) as any;
    const accessToken = String(res.accessToken);
    const refreshToken = String(res.refreshToken);
    const backendRole = String(res.user?.role ?? "STUDENT") as BackendRole;
    const userType = String(res.user?.userType ?? "user") as "user" | "admin";

    const permissions = await fetchMyPermissionsWithAccessToken(accessToken);

    const nextUser: AuthUser = {
      id: Number(res.user?.id),
      email: String(res.user?.email ?? payload.email ?? ""),
      backendRole,
      role: mapRole(backendRole),
      userType,
      permissions,
    };
    const nextTokens: Tokens = { accessToken, refreshToken };
    setUser(nextUser);
    setTokens(nextTokens);
    persist({ user: nextUser, tokens: nextTokens });
  }, []);

  const logout = useCallback(async () => {
    try {
      if (tokens?.refreshToken) {
        await apiFetch("/auth/user/logout", { method: "POST", json: { refreshToken: tokens.refreshToken } });
      } else {
        await apiFetch("/auth/user/logout", { method: "POST" });
      }
    } catch {
      // ignore network failure; still clear local auth
    } finally {
      clearAuth();
    }
  }, [clearAuth, tokens?.refreshToken]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, tokens, isHydrating, login, requestRegistrationOtp, register, logout }),
    [user, tokens, isHydrating, login, requestRegistrationOtp, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

